<template>
  <Button
    v-tooltip.bottom="tooltip"
    :label="showLabel ? buttonType.label : null"
    :loading="loading"
    :disabled="loading || disabled"
    :icon="showIcon ? buttonType.primeVueIcon || primeVueIcon : null"
    :class="buttonType.class"
    class="mt-auto"
    :text="text"
    :link="link"
    :v-ripple="vRipple"
    :variant="variant"
    @click="emit('click', $event)"
  >
    <template v-if="showIcon && buttonType.fontAwesomeIcon" #icon>
      <FontAwesomeIcon
        :icon="buttonType.fontAwesomeIcon"
        :class="iconPos === 'right' ? 'p-button-icon-right' : ''"
      />
    </template>
  </Button>
</template>

<script setup>
import { computed } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BUTTON_TYPES } from "@/Constants";
import Button from "primevue/button";

const emit = defineEmits(["click"]);

const props = defineProps({
  label: {
    type: String,
    default: null,
  },
  icon: {
    type: String,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "default",
  },
  showIcon: {
    type: Boolean,
    default: true,
  },
  showLabel: {
    type: Boolean,
    default: true,
  },
  tooltip: {
    type: String,
    default: "",
  },
  text: {
    type: Boolean,
    default: false,
  },
  additionalClass: {
    type: String,
    default: "",
  },
  overrideClass: {
    type: String,
    default: "",
  },
  link: {
    type: Boolean,
    default: false,
  },
  vRipple: {
    type: Boolean,
    default: false,
  },
  iconPos: {
    type: String,
    default: "left",
  },
  variant: {
    type: String,
    default: null,
  },
});

const BUTTON_TYPE_MAP = {
  // Primary Actions (Blue) - Main actions that move the workflow forward
  [BUTTON_TYPES.Add]: {
    defaultLabel: "rs_add",
    fontAwesomeIcon: "fa-plus",
    class: "p-button-primary",
  },
  [BUTTON_TYPES.Confirm]: {
    defaultLabel: "rs_confirm",
    fontAwesomeIcon: "fa-check",
    class: "p-button-primary",
  },
  [BUTTON_TYPES.Details]: {
    defaultLabel: "rs_details",
    fontAwesomeIcon: "fa-circle-info",
    class: "p-button-primary",
  },
  [BUTTON_TYPES.FileCheck]: {
    defaultLabel: "rs_file_check",
    fontAwesomeIcon: "fa-file",
    class: "p-button-primary",
  },
  [BUTTON_TYPES.ExcelFileCheck]: {
    defaultLabel: "rs_excel_file_check",
    fontAwesomeIcon: "fa-file-excel",
    class: "p-button-primary",
  },
  [BUTTON_TYPES.Transfer]: {
    defaultLabel: "rs_transfer",
    fontAwesomeIcon: "fa-right-left",
    class: "p-button-primary",
  },
  [BUTTON_TYPES.Import]: {
    defaultLabel: "rs_import",
    fontAwesomeIcon: "fa-file-import",
    class: "p-button-primary",
  },
  [BUTTON_TYPES.List]: {
    defaultLabel: "rs_list",
    fontAwesomeIcon: "fa-list",
    class: "p-button-primary",
  },
  [BUTTON_TYPES.OK]: {
    defaultLabel: "rs_ok",
    fontAwesomeIcon: "fa-check",
    class: "p-button-primary",
  },
  [BUTTON_TYPES.Clone]: {
    defaultLabel: "rs_clone",
    fontAwesomeIcon: "fa-clone",
    class: "p-button-primary",
  },

  // Success Actions (Green) - Completion/Confirmation actions
  [BUTTON_TYPES.Save]: {
    defaultLabel: "rs_save",
    fontAwesomeIcon: "fa-save",
    class: "p-button-success",
  },
  [BUTTON_TYPES.Accept]: {
    defaultLabel: "rs_accept",
    fontAwesomeIcon: "fa-check-circle",
    class: "p-button-success",
  },
  [BUTTON_TYPES.Posted]: {
    defaultLabel: "rs_posted",
    fontAwesomeIcon: "fa-check-square",
    class: "p-button-success",
  },
  [BUTTON_TYPES.GenerateExcelFile]: {
    defaultLabel: "rs_generate",
    fontAwesomeIcon: "fa-file-excel",
    class: "p-button-success",
  },
  [BUTTON_TYPES.Export]: {
    defaultLabel: "rs_export",
    fontAwesomeIcon: "fa-arrow-up-right-from-square",
    class: "p-button-success",
  },
  [BUTTON_TYPES.GenerateReport]: {
    defaultLabel: "rs_generate_report",
    fontAwesomeIcon: "fa-chart-line",
    class: "p-button-success",
  },
  [BUTTON_TYPES.Update]: {
    defaultLabel: "rs_update",
    fontAwesomeIcon: "fa-pen-to-square",
    class: "p-button-success",
  },
  [BUTTON_TYPES.Generate]: {
    defaultLabel: "rs_generate",
    fontAwesomeIcon: "fa-wand-magic-sparkles",
    class: "p-button-success",
  },
  [BUTTON_TYPES.Activate]: {
    defaultLabel: "rs_activate",
    fontAwesomeIcon: "fa-lock-open",
    class: "p-button-success",
  },
  [BUTTON_TYPES.Check]: {
    defaultLabel: "rs_check_all_boxes",
    fontAwesomeIcon: "fa-check-square",
    class: "p-button-success",
  },

  // Info Actions (Cyan) - View/Download operations
  [BUTTON_TYPES.Preview]: {
    defaultLabel: "rs_preview",
    fontAwesomeIcon: "fa-eye",
    class: "p-button-info",
  },
  [BUTTON_TYPES.Download]: {
    defaultLabel: "rs_download",
    fontAwesomeIcon: "fa-solid fa-download",
    class: "p-button-info",
  },
  [BUTTON_TYPES.Upload]: {
    defaultLabel: "rs_upload",
    fontAwesomeIcon: "fa-solid fa-upload",
    class: "p-button-info",
  },
  [BUTTON_TYPES.QrCode]: {
    defaultLabel: "rs_qr_code",
    fontAwesomeIcon: "fa-qrcode",
    class: "p-button-info",
  },
  [BUTTON_TYPES.Share]: {
    defaultLabel: "rs_share",
    fontAwesomeIcon: "fa-share-nodes",
    class: "p-button-info",
  },

  // Warning Actions (Orange) - Modifications/Changes
  [BUTTON_TYPES.Edit]: {
    defaultLabel: "rs_edit",
    fontAwesomeIcon: "fa-edit",
    class:
      props.text || props.link
        ? "p-button-warning"
        : "p-button-warning  !text-white",
  },
  [BUTTON_TYPES.Config]: {
    defaultLabel: "rs_configure",
    fontAwesomeIcon: "fa-cog",
    class:
      props.text || props.link
        ? "p-button-warning"
        : "p-button-warning  !text-white",
  },
  [BUTTON_TYPES.Note]: {
    defaultLabel: "",
    fontAwesomeIcon: "fa-sticky-note",
    class:
      props.text || props.link
        ? "p-button-warning"
        : "p-button-warning  !text-white",
  },
  [BUTTON_TYPES.Send]: {
    defaultLabel: "rs_send",
    fontAwesomeIcon: "fa-paper-plane",
    class:
      props.text || props.link
        ? "p-button-warning"
        : "p-button-warning  !text-white",
  },
  [BUTTON_TYPES.SendEmail]: {
    defaultLabel: "rs_send_email",
    fontAwesomeIcon: "fa-envelope",
    class:
      props.text || props.link
        ? "p-button-warning"
        : "p-button-warning  !text-white",
  },
  [BUTTON_TYPES.Stop]: {
    defaultLabel: "rs_stop",
    fontAwesomeIcon: "fa-stop",
    class:
      props.text || props.link
        ? "p-button-warning"
        : "p-button-warning  !text-white",
  },

  // Danger Actions (Red) - Destructive/Rejection actions
  [BUTTON_TYPES.Delete]: {
    defaultLabel: "rs_delete",
    fontAwesomeIcon: "fa-trash",
    class: "p-button-danger",
  },
  [BUTTON_TYPES.Reject]: {
    defaultLabel: "rs_reject",
    fontAwesomeIcon: "fa-times-circle",
    class: "p-button-danger",
  },
  [BUTTON_TYPES.Archive]: {
    defaultLabel: "rs_archive",
    fontAwesomeIcon: "fa-box-archive",
    class: "p-button-danger",
  },
  [BUTTON_TYPES.Deactivate]: {
    defaultLabel: "rs_deactivate",
    fontAwesomeIcon: "fa-lock",
    class: "p-button-danger",
  },
  [BUTTON_TYPES.Uncheck]: {
    defaultLabel: "rs_uncheck_all_boxes",
    fontAwesomeIcon: "fa-square",
    class: "p-button-danger",
  },
  [BUTTON_TYPES.Play]: {
    defaultLabel: "rs_play",
    fontAwesomeIcon: "fa-play",
    class: "p-button-danger",
  },
  [BUTTON_TYPES.Danger]: {
    defaultLabel: "rs_danger",
    fontAwesomeIcon: "fa-exclamation-triangle",
    class: "p-button-danger",
  },

  // Help/Status Actions (Purple)
  [BUTTON_TYPES.Pending]: {
    defaultLabel: "rs_pending",
    fontAwesomeIcon: "fa-clock",
    class: "p-button-help",
  },
  [BUTTON_TYPES.Password]: {
    defaultLabel: "rs_password",
    fontAwesomeIcon: "fa-key",
    class: "p-button-help",
  },

  // Secondary/Utility Actions (Gray) - Supporting operations
  [BUTTON_TYPES.Cancel]: {
    defaultLabel: "rs_cancel",
    fontAwesomeIcon: "fa-times",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Calculator]: {
    defaultLabel: "rs_calculator",
    fontAwesomeIcon: "fa-calculator",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Search]: {
    defaultLabel: "rs_search",
    fontAwesomeIcon: "fa-search",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Refresh]: {
    defaultLabel: "rs_refresh",
    fontAwesomeIcon: "fa-refresh",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Secondary]: {
    defaultLabel: "",
    fontAwesomeIcon: null,
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Back]: {
    defaultLabel: "rs_back",
    fontAwesomeIcon: "fa-arrow-left",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Next]: {
    defaultLabel: "rs_next",
    fontAwesomeIcon: "fa-arrow-right",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Finish]: {
    defaultLabel: "rs_finish",
    fontAwesomeIcon: "fa-flag-checkered",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Close]: {
    defaultLabel: "rs_close",
    fontAwesomeIcon: "fa-times",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Reset]: {
    defaultLabel: "rs_reset",
    fontAwesomeIcon: "fa-undo",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Copy]: {
    defaultLabel: "rs_copy",
    fontAwesomeIcon: "fa-copy",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Undo]: {
    defaultLabel: "rs_undo",
    fontAwesomeIcon: "fa-undo",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Link]: {
    defaultLabel: "rs_link",
    fontAwesomeIcon: "fa-link",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Clear]: {
    defaultLabel: "rs_clear",
    fontAwesomeIcon: "fa-eraser",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Sync]: {
    defaultLabel: "rs_sync",
    fontAwesomeIcon: "fa-sync",
    class: "p-button-secondary",
  },
  [BUTTON_TYPES.Logout]: {
    defaultLabel: "Abmelden",
    fontAwesomeIcon: "fa-solid fa-arrow-right-from-bracket",
    class: "p-button-secondary",
  },
};

const buttonType = computed(() => {
  const type = BUTTON_TYPE_MAP[props.type] || {
    defaultLabel: "",
    fontAwesomeIcon: "",
    primeVueIcon: "",
    class: "",
  };

  return {
    label: props.showLabel ? (props.label ?? type.defaultLabel) : null,
    fontAwesomeIcon: props.showIcon ? type.fontAwesomeIcon : null,
    primeVueIcon: props.showIcon ? type.primeVueIcon : null,
    class:
      props.overrideClass ||
      (props.additionalClass
        ? `${type.class} ${props.additionalClass}`
        : type.class),
  };
});

const primeVueIcon = computed(() => {
  if (typeof props.icon === "string" && props.icon.startsWith("pi")) {
    return props.icon;
  }
  return null;
});
</script>
